<script setup lang="ts">
import {ref} from "vue";
import {getGroupInvitation} from "@/Services/group-members";
import {getInvitation, respondToInvitation} from "@/Services/invitations";
import {getGroup} from "@/Services/groups";
import {useRouter} from "vue-router";
import {getting} from "@/Composables/api";
import {Avatar, Button, ImportantInfoPanel, Message} from "@/Components/Library";
import {PageLoading} from "@/Components/Global";
import User from "../../../Models/User";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import {useMainStore} from "@/Stores/store";
//TODO fix back button after redirect to group on accept
const router = useRouter();
const user = new User();
const confirm = useConfirm();
const toast = useToast();
const $store = useMainStore();
const group = ref();
const invitation = ref();
const loading = ref(false);
getInvitation(router.currentRoute.value.params.invitation, {}, false)
    .then(({data}) => {
        invitation.value = data;
        getGroup(router.currentRoute.value.params.group, {invite: true}).then(({data}) => (group.value = data));
    })
    .catch((e) => {
        console.log(e);
        router.push({name: "groups.index"});
    });
const accept = () => {
    confirm.require({
        header: "Accept invitation",
        message: invitation.value.is_connected
            ? "Are you sure you want to join this group?"
            : `Are you sure you want to join this group and add ${group.value.user.first_name} to your contacts?`,
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "text-red-600 px-2 py-1 rounded-lg hover:brightness-110",
        accept: () => {
            loading.value = true;
            respondToInvitation(invitation.value.id, "accept")
                .then((resp) => {
                    $store.decrementGroupInvitations();
                    $store.groups.push(group.value);
                    router.push({name: "feed", params: {group: group.value.id}});
                })
                .catch((error) => {
                    loading.value = false;
                    console.error(error);
                });
        },
    });
};

const decline = () => {
    confirm.require({
        header: "Decline invitation",
        message: `Are you sure you want to decline this invitation?`,
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 rounded-lg hover:brightness-110",
        accept: () => {
            respondToInvitation(invitation.value.id, "decline").then((resp) => {
                $store.decrementGroupInvitations();
                toast.add({
                    severity: "secondary",
                    summary: "Invitation declined",
                    detail: `You have declined the invitation to join ${group.value.name}`,
                    life: 5000,
                });
                router.push({name: "groups.index"});
            });
        },
    });
};
</script>
<template>
    <div class="mx-auto max-w-5xl">
        <PageLoading v-if="getting.includes('groups')" class="pt-16" />
        <div v-if="group" class="pt-16">
            <div class="mb-3 flex items-center justify-center gap-2 text-xl">
                <Avatar :user="group.user" class="h-8 w-8 flex-none shrink-0 text-xs" shape="circle" />
                <div>
                    <span class="font-medium" v-if="group.user.first_name">{{ group.user.first_name }}</span>
                    <span class="ml-1 font-bold" v-if="group.user.last_name">{{ group.user.last_name }}</span>
                </div>
            </div>
            <p class="mb-8 text-center text-lg">is inviting you to a group</p>
            <p class="mx-auto mb-8 max-w-md text-center">
                By accepting this invite you will become {{ invitation.is_connected ? "" : "their contact and" }} a
                member of this group.
            </p>
            <div class="m-container mb-2 flex flex-col items-center">
                <Avatar
                    class="mb-3 h-12 w-12 text-xl"
                    :class="[`bg-${group.color?.class}`, group.color?.class === 'white' ? 'text-black' : 'text-white']"
                    :label="group.icon ? undefined : group.initials"
                    :image="group.icon?.url"
                />
                <h2 class="my-1 text-lg font-bold sm:text-2xl">
                    {{ group.name }}
                </h2>
                <!--                <TagList :tags="group.tags" class="mb-2" center />-->
            </div>
            <p class="m-container mb-4">{{ group.description }}</p>
            <ImportantInfoPanel v-if="group.important_info" :content="group.important_info" class="m-container mb-6" />
            <div class="flex flex-col gap-2">
                <Message v-if="invitation.status === 'declined'">You have already declined this invitation.</Message>
                <Button
                    :disabled="invitation.status === 'declined'"
                    :loading="loading"
                    @click="accept"
                    class="w-full bg-askr-green text-white"
                    icon="fluent:checkmark-12-regular"
                >
                    Accept invite
                </Button>
                <Button
                    :disabled="invitation.status === 'declined'"
                    @click="decline"
                    class="w-full"
                    icon="fluent:delete-12-regular"
                >
                    Decline invite
                </Button>
            </div>
        </div>
    </div>
</template>
<style scoped></style>
